"use client";

import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { Icon } from "@iconify/react";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { useWorkspaceContextWithFallback } from "@/contexts/workspaceContext";
import CustomEmojiUpload from "@/components/tailwind/common/CustomEmojiUpload";
import type { CustomEmoji } from "@/types/workspace";
import { DropdownMenuSearch } from "@/components/tailwind/ui/dropdown-menu";
import { POPOVER_PANEL_ATTR, SelfAnchoredPopover } from "@/components/tailwind/ui/anchored-popover";
import { ICON_COLORS, buildIconValue, iconColorCss, parseIconValue, supportsIconColor } from "./renderIcon";

interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
  onClose: () => void;
  onRemove?: () => void;
  currentEmoji?: string;
  anchorRef?: React.RefObject<HTMLElement>;
  anchorRect?: DOMRect | null;
  position?: "fixed" | "absolute";
  /** Tab shown on open; defaults to "icons" (page-icon flows). Reactions open on "emoji". */
  initialTab?: MainTab;
}

import { EMOJI_CATEGORIES, UI_ICONS, FLAT_COLOR_ICONS, EMOJI_NAMES } from "./emojiPickerData";


// ─── Component ────────────────────────────────────────────────────────────────

type MainTab = "emoji" | "icons" | "custom" | "upload";

export default function EmojiPicker({
  onSelect,
  onClose,
  onRemove,
  currentEmoji,
  anchorRef,
  anchorRect,
  position = "fixed",
  initialTab,
}: EmojiPickerProps) {
  const [mainTab, setMainTab] = useState<MainTab>(initialTab ?? "icons");
  const [searchTerm, setSearchTerm] = useState("");
  // Colour for monochrome icons; null = inherit. Seeded from the icon in use so
  // reopening the picker shows the current choice.
  const [iconColor, setIconColor] = useState<string | null>(() =>
    currentEmoji ? parseIconValue(currentEmoji).color : null,
  );
  const [showColorMenu, setShowColorMenu] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>("Recent");
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const pickerRef = useRef<HTMLDivElement>(null);

  // Custom emojis are selection-only here; managed from Settings → Workspace → Emoji.
  // WithFallback: the picker also mounts in detached React roots (vanilla PM
  // node views, e.g. the callout icon) where context can't reach it.
  const currentWorkspace = useWorkspaceContextWithFallback()?.currentWorkspace ?? null;

  const customEmojis: CustomEmoji[] = currentWorkspace?.customEmojis ?? [];

  // The Custom tab only exists while the workspace has custom emojis; fall
  // back if the active tab disappears (e.g. last emoji deleted elsewhere).
  const mainTabs: MainTab[] = [
    "icons",
    "emoji",
    ...(customEmojis.length > 0 ? (["custom"] as MainTab[]) : []),
    "upload",
  ];
  useEffect(() => {
    if (mainTab === "custom" && customEmojis.length === 0) setMainTab("icons");
  }, [mainTab, customEmojis.length]);


  // ── Emoji filtering ───────────────────────────────────────────────────────

  const filteredEmojis = useMemo(() => {
    if (!searchTerm) return EMOJI_CATEGORIES;
    const q = searchTerm.toLowerCase();
    const filtered: Record<string, string[]> = {};
    Object.entries(EMOJI_CATEGORIES).forEach(([category, emojis]) => {
      const matches = emojis.filter((emoji) =>
        (EMOJI_NAMES[emoji] ?? "").toLowerCase().includes(q)
      );
      if (matches.length > 0) filtered[category] = matches;
    });
    return filtered;
  }, [searchTerm]);

  // Custom emojis matching the current filter (shared search box).
  const filteredCustom = useMemo(() => {
    if (!searchTerm) return customEmojis;
    const q = searchTerm.toLowerCase();
    return customEmojis.filter((e) => e.name.toLowerCase().includes(q));
  }, [searchTerm, customEmojis]);

  // ── Icon filtering ────────────────────────────────────────────────────────

  const filteredUI = useMemo(() => {
    if (!searchTerm) return UI_ICONS;
    return UI_ICONS.filter((n) => n.includes(searchTerm.toLowerCase()));
  }, [searchTerm]);

  const filteredFlat = useMemo(() => {
    if (!searchTerm) return FLAT_COLOR_ICONS;
    return FLAT_COLOR_ICONS.filter((n) => n.includes(searchTerm.toLowerCase()));
  }, [searchTerm]);

  // ── Scroll to emoji category ───────────────────────────────────────────────

  const scrollToCategory = useCallback((category: string) => {
    const element = categoryRefs.current[category];
    if (element) element.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // ── Positioning — computed synchronously on first render to avoid flicker ──

  const computeStyle = (buttonRect: DOMRect): React.CSSProperties => {
    const pickerHeight = 390;
    const gap = 8;
    const maxPickerWidth = 408;
    const minPickerWidth = 300;

    const spaceBelow = window.innerHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    const showBelow = spaceBelow >= pickerHeight || spaceBelow >= spaceAbove;
    const pickerWidth = Math.max(minPickerWidth, Math.min(maxPickerWidth, window.innerWidth - 16));

    const top = showBelow
      ? buttonRect.bottom + gap
      : Math.max(gap, buttonRect.top - pickerHeight - gap);
    let left = buttonRect.left;
    if (left + pickerWidth > window.innerWidth - gap) {
      left = Math.max(gap, buttonRect.right - pickerWidth);
    }

    return { top: `${top}px`, left: `${left}px`, width: `${pickerWidth}px` };
  };

  const [pickerStyle, setPickerStyle] = useState<React.CSSProperties>(() => {
    if (typeof window === "undefined") return {};
    const buttonRect = anchorRect ?? anchorRef?.current?.getBoundingClientRect();
    if (!buttonRect) return {};
    return computeStyle(buttonRect);
  });

  const isPositioned = !!(anchorRect ?? anchorRef?.current);

  useEffect(() => {
    const update = () => {
      const buttonRect = anchorRect ?? anchorRef?.current?.getBoundingClientRect();
      if (!buttonRect) return;
      setPickerStyle(computeStyle(buttonRect));
    };

    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On phones, override both the anchored-popover positioning AND the
  // centered-modal layout in favor of a bottom sheet —
  // full-width, anchored to the bottom edge, with a dimmed backdrop above.
  // Easier thumb reach, plays better with the iOS soft keyboard, and matches
  // how a mobile emoji picker behaves.
  const isMobile = useIsMobile();

  // The reposition-on-scroll effect above re-measures the anchor — which only
  // works for live anchorRef callers. A static anchorRect can't be
  // re-measured, so on outside scroll the anchor moves and the picker would
  // detach: close it instead. Scrolls inside the picker (emoji grid) don't
  // count, and the mobile bottom sheet doesn't anchor to a rect at all.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (isMobile || !anchorRect || anchorRef?.current) return;
    const onScroll = (e: Event) => {
      if (e.target instanceof Node && pickerRef.current?.contains(e.target)) return;
      onCloseRef.current();
    };
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [isMobile, anchorRect, anchorRef]);

  // z-10000/10001 on every variant: the picker opens from comments inside
  // the peek panel (z-200) and the inline thread popup (z-9999) — it must
  // clear both, staying under the z-99999 tooltip layer.
  const backdropClass = isMobile
    ? "fixed inset-0 z-[10000] bg-black/40"
    : isPositioned
      ? "fixed inset-0 z-[10000]"
      : "fixed inset-0 z-[10000] flex items-start justify-center pt-20 bg-black/20 dark:bg-black/40";
  // The Upload tab's content is short; a content-sized picker beats a mostly
  // empty fixed-height one there.
  const isCompact = mainTab === "upload";

  const pickerClass = isMobile
    ? "fixed inset-x-0 bottom-0 z-[10001] bg-modal rounded-t-lg shadow-2xl overflow-hidden flex flex-col"
    : isPositioned
      ? "fixed z-[10001] bg-modal border border-component rounded-lg shadow-2xl overflow-hidden flex flex-col"
      : `bg-modal rounded-lg shadow-2xl overflow-hidden w-[408px] max-w-[calc(100vw-24px)] ${isCompact ? "h-auto" : "h-[390px]"} max-h-[70vh] flex flex-col`;

  // ── Helpers ───────────────────────────────────────────────────────────────

  const handleSelect = (value: string) => {
    onSelect(value);
    onClose();
  };

  /**
   * Picking a colour recolours the icon already in use, so it takes effect
   * immediately instead of waiting for the user to reselect the same glyph.
   * With an emoji or image selected there is nothing to recolour — the choice
   * just applies to the next monochrome icon picked.
   */
  const handleColorSelect = (color: string | null) => {
    setIconColor(color);
    setShowColorMenu(false);
    if (currentEmoji && supportsIconColor(currentEmoji)) {
      handleSelect(buildIconValue(parseIconValue(currentEmoji).icon, color));
    }
  };

  return (
    <>
    <div className={backdropClass} onClick={onClose}>
      <div
        ref={pickerRef}
        {...{ [POPOVER_PANEL_ATTR]: "" }}
        className={pickerClass}
        style={
          isMobile
            ? // Bottom-sheet: full width, capped at 80% of dynamic viewport
              // so it leaves a visible backdrop above the sheet.
              { height: isCompact ? "auto" : "min(80dvh, 480px)", maxHeight: "80dvh" }
            : isPositioned
              ? { ...pickerStyle, height: isCompact ? "auto" : "390px", maxHeight: "70vh", minWidth: "300px" }
              : undefined
        }
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Top bar: main tabs + remove ──────────────────────────────── */}
        <div className="flex items-center border-b border-component px-2">
          <div className="flex gap-1 py-1.5 flex-1">
            {mainTabs.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => {
                  setMainTab(tab);
                  setSearchTerm("");
                }}
                className={`px-3 h-7 rounded-md text-sm font-medium transition-colors capitalize ${
                  mainTab === tab
                    ? "bg-gray-100 dark:bg-zinc-800 text-gray-900 dark:text-white"
                    : "text-sec hover-component "
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
          {currentEmoji && onRemove && (
            <button
              type="button"
              onClick={() => {
                onRemove();
                onClose();
              }}
              className="flex-shrink-0 px-2 h-7 rounded-md text-sm text-sec hover-component  transition-colors"
            >
              Remove
            </button>
          )}
        </div>

        {mainTab === "emoji" ? (
          <>
            {/* ── Emoji category sub-tabs ─────────────────────────────── */}
            <div className="flex items-center border-b border-component px-2 overflow-x-auto clean-scrollbar">
              <div className="flex gap-0.5 py-1 min-w-0">
                {Object.keys(EMOJI_CATEGORIES).map((category) => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => {
                      setActiveCategory(category);
                      scrollToCategory(category);
                    }}
                    className={`px-2 h-6 rounded text-xs transition-colors whitespace-nowrap ${
                      activeCategory === category
                        ? "bg-gray-100 dark:bg-zinc-800 text-gray-900 dark:text-white font-medium"
                        : "text-sec hover-component "
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Search ──────────────────────────────────────────────── */}
            <div className="px-2 py-1.5">
              <DropdownMenuSearch
                placeholder="Filter…"
                value={searchTerm}
                onChange={setSearchTerm}
              />
            </div>

            {/* ── Emoji grid ──────────────────────────────────────────── */}
            <div className="relative flex-1 min-h-0">
              {/* top fade */}
              <div className="pointer-events-none absolute inset-x-0 top-0 h-9 z-10 bg-gradient-to-b from-[var(--modal-bg)] to-transparent" />
              {/* bottom fade */}
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-9 z-10 bg-gradient-to-t from-[var(--modal-bg)] to-transparent" />
              <div className="h-full overflow-y-auto px-3 pb-2 clean-scrollbar">
              {Object.entries(filteredEmojis).map(([category, emojis]) => (
                <div
                  key={category}
                  className="mb-4"
                  ref={(el) => { categoryRefs.current[category] = el; }}
                >
                  <div className="text-xs font-medium text-sec mb-2 px-1 pt-2">
                    {category}
                  </div>
                  <div className="grid grid-cols-10 gap-1">
                    {emojis.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => handleSelect(emoji)}
                        className="w-8 h-8 flex items-center justify-center rounded hover-component  transition-colors text-xl leading-none select-none"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              </div>
            </div>
          </>
        ) : mainTab === "custom" ? (
          <>
            {/* ── Custom emoji search ──────────────────────────────────── */}
            <div className="px-2 py-1.5">
              <DropdownMenuSearch
                placeholder="Filter…"
                value={searchTerm}
                onChange={setSearchTerm}
              />
            </div>

            {/* ── Custom emoji grid ────────────────────────────────────── */}
            <div className="relative flex-1 min-h-0">
              <div className="h-full overflow-y-auto px-3 pb-2 clean-scrollbar">
                {filteredCustom.length === 0 ? (
                  <div className="flex items-center justify-center h-24 text-sm text-gray-400 px-4 text-center">
                    No custom emojis match your search.
                  </div>
                ) : (
                  <div className="grid grid-cols-10 gap-1 pt-2">
                    {filteredCustom.map((emoji) => (
                      <button
                        key={emoji._id}
                        type="button"
                        onClick={() => handleSelect(emoji.imageUrl)}
                        className="w-8 h-8 flex items-center justify-center rounded overflow-hidden hover-component  transition-colors"
                      >
                        <img src={emoji.imageUrl} alt={emoji.name} className="w-6 h-6 object-cover rounded" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : mainTab === "upload" ? (
          <CustomEmojiUpload
            optionalLibraryAdd
            backOnPreview
            onCancel={onClose}
            onSaved={(url) => {
              onSelect(url);
              onClose();
            }}
            className="flex-1 min-h-0 overflow-y-auto clean-scrollbar px-4"
          />
        ) : (
          <>
            {/* ── Icon search + colour ─────────────────────────────────── */}
            <div className="px-2 py-1.5 flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <DropdownMenuSearch
                  placeholder="Search icons…"
                  value={searchTerm}
                  onChange={setSearchTerm}
                />
              </div>

              {/* Colour applies to the monochrome "Solid" set only — the
                  Colored set is multi-colour by design and ignores it. */}
              <div className="relative flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowColorMenu((open) => !open)}
                  aria-label="Icon colour"
                  aria-expanded={showColorMenu}
                  title="Icon colour"
                  className="h-8 w-8 flex items-center justify-center rounded-md border border-component hover-component transition-colors"
                >
                  {/* Transparent when no colour is set, matching the "Default"
                      swatch in the menu — it read as solid black otherwise. */}
                  <span
                    className="h-4 w-4 rounded-full border border-component"
                    style={{ background: iconColorCss(iconColor) ?? "transparent" }}
                  />
                </button>

                {showColorMenu && (
                  <SelfAnchoredPopover
                    onClose={() => setShowColorMenu(false)}
                    placement="bottom-end"
                    zIndex={10002}
                    className="bg-modal border border-component rounded-lg shadow-lg p-2 w-[168px]"
                  >
                      <div className="grid grid-cols-5 gap-1">
                        <button
                          type="button"
                          title="Default"
                          onClick={() => handleColorSelect(null)}
                          className={`h-7 w-7 flex items-center justify-center rounded hover-component transition-colors ${
                            iconColor === null ? "ring-2 ring-blue-500" : ""
                          }`}
                        >
                          <span className="h-4 w-4 rounded-full border border-component bg-transparent" />
                        </button>
                        {ICON_COLORS.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            title={c.label}
                            onClick={() => handleColorSelect(c.id)}
                            className={`h-7 w-7 flex items-center justify-center rounded hover-component transition-colors ${
                              iconColor === c.id ? "ring-2 ring-blue-500" : ""
                            }`}
                          >
                            <span
                              className="h-4 w-4 rounded-full border border-component"
                              style={{ background: c.cssValue }}
                            />
                          </button>
                        ))}
                      </div>
                  </SelfAnchoredPopover>
                )}
              </div>
            </div>

            {/* ── Icon sections ─────────────────────────────────────────── */}
            <div className="relative flex-1 min-h-0">
              {/* top fade */}
              <div className="pointer-events-none absolute inset-x-0 top-0 h-9 z-10 bg-gradient-to-b from-[var(--modal-bg)] to-transparent" />
              {/* bottom fade */}
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-9 z-10 bg-gradient-to-t from-[var(--modal-bg)] to-transparent" />
              <div className="h-full overflow-y-auto px-3 pb-2 clean-scrollbar">

              {/* User Interface Icons (Tabler + Boxicons) */}
              {filteredUI.length > 0 && (
                <div className="mb-5">
                  <div className="text-xs font-medium text-sec mb-2 px-1 pt-2">
                    Solid
                  </div>
                  <div className="grid grid-cols-9 gap-1">
                    {filteredUI.map((name) => {
                      const iconId = name;
                      return (
                        <button
                          key={iconId}
                          type="button"
                          title={name.includes(":") ? name.split(":")[1] : name}
                          // The chosen colour is stored with the icon, so the
                          // grid previews exactly what gets saved.
                          onClick={() => handleSelect(buildIconValue(iconId, iconColor))}
                          className="w-8 h-8 flex items-center justify-center rounded hover-component  transition-colors text-gray-700 dark:text-gray-300"
                          style={{ color: iconColorCss(iconColor) }}
                        >
                          <Icon icon={iconId} width={22} height={22} />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Colored (Flat Color Icons) */}
              {filteredFlat.length > 0 && (
                <div className="mb-2">
                  <div className="text-xs font-medium text-sec mb-2 px-1 pt-1">
                    Colored
                  </div>
                  <div className="grid grid-cols-9 gap-1">
                    {filteredFlat.map((name) => {
                      const iconId = `flat-color-icons:${name}`;
                      return (
                        <button
                          key={iconId}
                          type="button"
                          title={name}
                          onClick={() => handleSelect(iconId)}
                          className="w-8 h-8 flex items-center justify-center rounded hover-component  transition-colors"
                        >
                          <Icon icon={iconId} width={22} height={22} />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {filteredUI.length === 0 && filteredFlat.length === 0 && (
                <div className="flex items-center justify-center h-24 text-sm text-gray-400">
                  No icons found
                </div>
              )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
    </>
  );
}
